import { useEffect, useState } from 'react';
import { subscribeInvoice, subscribeInvoicesForPeriod } from '@/data/invoicesRepo';
import type { InvoiceType } from '@/types/types_invoice';

/**
 * Both hooks here keep *what they loaded and what it was for*, rather than a
 * separate `loading` flag reset when the key changes.
 *
 * Which is not a style choice. These are the only subscriptions in the app whose
 * key changes while the page stays mounted — an admin steps from June to July —
 * and a plain flag would leave June's invoices on screen labelled July until
 * Firestore answered. Deriving both from the same stored key means the data and
 * the month it belongs to cannot be shown disagreeing, even for a frame.
 */

const noInvoices: ReadonlyMap<string, InvoiceType> = new Map();

/**
 * Every invoice raised for one month, keyed by partner so the list page can pair
 * them with the partner records without a lookup per row.
 *
 * Live, because the list is what an admin watches while generating drafts for a
 * dozen partners in a row — each should appear as it lands rather than on a reload.
 */
export function useInvoicesForPeriod(period: string) {
  const [loaded, setLoaded] = useState<{ period: string; invoices: ReadonlyMap<string, InvoiceType> } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeInvoicesForPeriod(
      period,
      (list) => {
        setLoaded({ period, invoices: new Map(list.map((invoice) => [invoice.partnerId, invoice])) });
        setError(null);
      },
      (e) => {
        console.error('Invoice subscription failed', e);
        setError('Could not load invoices for this month.');
        // Marked loaded so the page stops saying "Loading…" over an error it is
        // already showing. An empty month and a month that failed to load are
        // told apart by `error`, not by an eternal spinner.
        setLoaded({ period, invoices: noInvoices });
      },
    );
  }, [period]);

  const current = loaded?.period === period ? loaded : null;
  return { invoices: current?.invoices ?? noInvoices, loading: !current, error };
}

/** One invoice, by its derived id. Live, so issuing it updates the page. */
export function useInvoice(invoiceId: string) {
  const [loaded, setLoaded] = useState<{ invoiceId: string; invoice: InvoiceType | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeInvoice(
      invoiceId,
      (invoice) => {
        setLoaded({ invoiceId, invoice });
        setError(null);
      },
      (e) => {
        console.error('Invoice subscription failed', e);
        setError('Could not load that invoice.');
        setLoaded({ invoiceId, invoice: null });
      },
    );
  }, [invoiceId]);

  const current = loaded?.invoiceId === invoiceId ? loaded : null;
  return { invoice: current?.invoice ?? null, loading: !current, error };
}
